import { useAppDispatch, useAppSelector } from '@/store/hooks';
import styles from '../../widget.module.css';
import { selectSessionVideos, selectWorkingProject, selectWorkingSession } from '@/store/slices/project/selectors';
import { useEffect, useMemo, useState } from 'react';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import ManageVodContentModal from '../../../project-overview/components/session-date-info/on-demand/manage-vod-content-modal';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { DeleteSecondarySessionVideo, UpdateSecondarySessionVideo } from '@/connection/videos/placeholder-video';
import toastTrigger from '@ui/toast/toast-trigger';
import { selectRecordings, selectUploadedVideos } from '@/store/slices/content/selectors';
import useLoadVideos from '../../../project-overview/components/session-date-info/on-demand/use-load-videos';
import VideoThumbnail from '@ui/video-thumbnail/video-thumbnail';
import { formatMsToHMMSS } from '@/utils/dates';
import Pill from '@ui/pill/pill';
import { transparentPng } from '@/utils/transparent-img';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';

const PreshowVideoWidget = () => {
  const videos = useAppSelector(selectSessionVideos);
  const workingProject = useAppSelector(selectWorkingProject);
  const workingSession = useAppSelector(selectWorkingSession);
  const recordings = useAppSelector(selectRecordings);
  const uploaded = useAppSelector(selectUploadedVideos);
  const { fetchVideos } = useLoadVideos();
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const placeholderVideo = useMemo(() => {
    return videos?.find((vid) => vid.language === 'all' && vid.type === 'placeholder_video');
  }, [videos]);

  const toggleModal = () => {
    setOpen((prevState) => !prevState);
  };

  const setSessionPlaceholder = async (uuid: string, playbackUrl: string | null | undefined) => {
    if (!workingProject) return;
    try {
      const sessionUuid = workingSession?.uuid ?? workingProject.sessions[0].uuid;
      const sessionId = workingSession?.session ?? workingProject.sessions[0].session;

      let sessionVideos = workingProject.sessionVideos ?? [];
      if (playbackUrl) {
        if (placeholderVideo) {
          sessionVideos = sessionVideos.map((vid) => {
            if (vid.type === 'placeholder_video' && vid.session_uuid === sessionUuid) {
              return {
                ...vid,
                playback_url: playbackUrl,
              };
            }
            return vid;
          });
        } else {
          sessionVideos = [
            ...sessionVideos,
            {
              session_uuid: sessionUuid,
              language: 'all',
              playback_url: playbackUrl,
              type: 'placeholder_video',
            },
          ];
        }
        await UpdateSecondarySessionVideo({
          static_video_uuid: uuid,
          session: sessionId,
          playback_url: playbackUrl,
        });
      } else {
        sessionVideos = sessionVideos.filter(
          (vid) => !(vid.session_uuid === sessionUuid && vid.type === 'placeholder_video')
        );
        await DeleteSecondarySessionVideo(sessionId);
      }

      dispatch(
        saveWorkingProjectByFields({
          sessionVideos,
        })
      );
    } catch (e) {
      console.error(e);
      toastTrigger('Unable to update pre-live video', { type: 'error' });
    }
  };

  const video = useMemo(() => {
    if (placeholderVideo && recordings && uploaded) {
      return (
        recordings.find((rec) => rec.playback_url === placeholderVideo.playback_url) ||
        uploaded.find((vid) => vid.playback_url === placeholderVideo.playback_url)
      );
    }
  }, [placeholderVideo, recordings, uploaded]);

  const renderButton = () => {
    if (placeholderVideo) {
      return (
        <Button
          backgroundColor={getThemeColorCSSVariable()}
          padding="16px 24px"
          height="32px"
          onClick={toggleModal}
          disabledOpacity={0.6}
          hoverOpacity={0.8}
          className={`${styles['edit-button']} flex ai-center gap-6`}
        >
          <Icon
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            name={ICONS.EDIT}
            size={12}
          ></Icon>
          <Text
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            className="L1"
          >
            Edit
          </Text>
        </Button>
      );
    }
    return (
      <Button
        onClick={toggleModal}
        backgroundColor="var(--gray-700)"
        hoverBackgroundColor="var(--gray-1000)"
        className={`${styles['add-button']} flex ai-center gap-6`}
        height="32px"
      >
        <Icon name={placeholderVideo ? ICONS.EDIT : ICONS.ADD} size={12} color={getThemeColorCSSVariable()} />
        <Text color={getThemeColorCSSVariable()} className="L2">
          Add
        </Text>
      </Button>
    );
  };

  return (
    <>
      <div className={styles['widget']}>
        <Container
          className="flex flex-column"
          background="var(--gray-900)"
          borderRadius="12px"
          gap="16px"
          padding="16px"
        >
          <Container className="flex ai-center jc-space-between">
            <Container className="flex gap-4 ai-center">
              <Text className="H6">Pre-Show Video</Text>
              <TooltipPortal
                childrenWrapperClass="flex"
                tooltipContent={
                  <Text color="var(--gray-true-dark-100)" maxWidth="250px">
                    Upload a video here to show on your session page ahead of stream start
                  </Text>
                }
              >
                <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-500)" />
              </TooltipPortal>
            </Container>
            {renderButton()}
          </Container>
          {video && (
            <Container borderRadius="4px" padding={'8px'} className="flex ai-center" gap="8px" design="GlobalCont2">
              <Container className="flex ai-center flex-1" gap="8px">
                <VideoThumbnail
                  errorElement={<img src={transparentPng} />}
                  className="radius-4 image-cover"
                  height="24px"
                  width="45px"
                  src={video.thumbnail_url}
                />
                <Text design="GlobalIcoText1" className="S2">
                  {video.name}
                </Text>
              </Container>
              <Container className="flex ai-center" gap="8px">
                <Text className="L2">{formatMsToHMMSS(video.duration_ms)}</Text>
                <Pill
                  backgroundColor="transparent"
                  borderStyle="solid"
                  borderColor="var(--local-chip-input-default-outline)"
                  borderWidth="1px"
                  padding="4px 8px"
                >
                  <Text className="L2" design="GlobalIcoText1">
                    MP4
                  </Text>
                </Pill>
              </Container>
            </Container>
          )}
        </Container>
      </div>
      <ManageVodContentModal
        onClose={() => {
          setOpen(false);
        }}
        onSave={setSessionPlaceholder}
        currentVideoPlaybackUrl={placeholderVideo?.playback_url}
        open={open}
      />
    </>
  );
};

export default PreshowVideoWidget;
